import React, { useState, useEffect } from 'react';
import { User, Mail, Building2, Shield, KeyRound, Eye, EyeOff, CheckCircle2, XCircle, AlertCircle } from 'lucide-react';
import API from '../api/api';

// Password requirement rule definition
const PASSWORD_RULES = [
  { id: 'length',    label: 'At least 8 characters',        test: (p) => p.length >= 8 },
  { id: 'upper',     label: 'At least one uppercase letter', test: (p) => /[A-Z]/.test(p) },
  { id: 'lower',     label: 'At least one lowercase letter', test: (p) => /[a-z]/.test(p) },
  { id: 'number',    label: 'At least one number',           test: (p) => /\d/.test(p) },
  { id: 'special',   label: 'At least one special character',test: (p) => /[^A-Za-z0-9]/.test(p) },
];

function PasswordStrengthBar({ password }) {
  const passed = PASSWORD_RULES.filter((r) => r.test(password)).length;
  const levels = [
    { min: 0, label: '',          color: 'bg-gray-200' },
    { min: 1, label: 'Weak',      color: 'bg-red-500' },
    { min: 3, label: 'Fair',      color: 'bg-yellow-500' },
    { min: 4, label: 'Good',      color: 'bg-primary-500' },
    { min: 5, label: 'Strong',    color: 'bg-green-500' },
  ];

  const current = [...levels].reverse().find((l) => passed >= l.min);
  const fillWidth = password.length === 0 ? 0 : Math.round((passed / PASSWORD_RULES.length) * 100);

  return (
    <div className="mt-2">
      <div className="h-1.5 w-full rounded-full bg-gray-200 overflow-hidden">
        <div
          className={`h-full rounded-full transition-all duration-300 ${current.color}`}
          style={{ width: `${fillWidth}%` }}
        />
      </div>
      {password.length > 0 && (
        <p className={`text-xs mt-1 font-medium ${current.color.replace('bg-', 'text-')}`}>
          {current.label}
        </p>
      )}
    </div>
  );
}

function RequirementItem({ rule, password }) {
  const met = rule.test(password);
  return (
    <li className={`flex items-center gap-1.5 text-xs ${met ? 'text-green-600' : 'text-gray-400'}`}>
      {met
        ? <CheckCircle2 size={13} className="shrink-0" />
        : <XCircle size={13} className="shrink-0" />}
      {rule.label}
    </li>
  );
}

function InfoRow({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-3 py-3 border-b border-gray-100 last:border-0">
      <div className="flex-shrink-0 mt-0.5 text-gray-400">
        <Icon size={16} />
      </div>
      <div className="min-w-0">
        <p className="text-xs text-gray-500 uppercase tracking-wide font-medium">{label}</p>
        <p className="text-sm text-gray-800 mt-0.5 break-all">{value || <span className="text-gray-400 italic">Not set</span>}</p>
      </div>
    </div>
  );
}

function PasswordField({ id, label, value, onChange, show, onToggleShow, autoComplete }) {
  return (
    <div>
      <label htmlFor={id} className="block text-sm font-medium text-gray-700 mb-1">
        {label}
      </label>
      <div className="relative">
        <input
          id={id}
          type={show ? 'text' : 'password'}
          value={value}
          onChange={onChange}
          autoComplete={autoComplete}
          className="block w-full pr-10 rounded-lg border border-gray-300 px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
        />
        <button
          type="button"
          onClick={onToggleShow}
          className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600"
          tabIndex={-1}
        >
          {show ? <EyeOff size={16} /> : <Eye size={16} />}
        </button>
      </div>
    </div>
  );
}

function AccountSettings() {
  const [user, setUser] = useState(null);
  const [loadingUser, setLoadingUser] = useState(true);

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const [saving, setSaving] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');
  const [errorMsg, setErrorMsg] = useState('');

  useEffect(() => {
    API.get('auth/user/')
      .then((res) => setUser(res.data))
      .catch(() => setUser(null))
      .finally(() => setLoadingUser(false));
  }, []);

  const allRulesMet = PASSWORD_RULES.every((r) => r.test(newPassword));
  const passwordsMatch = newPassword === confirmPassword && confirmPassword.length > 0;
  const canSubmit = currentPassword.length > 0 && allRulesMet && passwordsMatch && !saving;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSuccessMsg('');
    setErrorMsg('');
    setSaving(true);
    try {
      await API.post('auth/change-password/', {
        current_password: currentPassword,
        new_password: newPassword,
        confirm_password: confirmPassword,
      });
      setSuccessMsg('Password changed successfully.');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      const data = err.response?.data;
      if (data?.requirements) {
        setErrorMsg(`${data.error} Missing: ${data.requirements.join(', ')}.`);
      } else {
        setErrorMsg(data?.error || 'An error occurred. Please try again.');
      }
    } finally {
      setSaving(false);
    }
  };

  const ROLE_LABELS = { admin: 'Admin', dispatcher: 'Dispatcher', driver: 'Driver' };

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8">
      {/* Page header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Account Settings</h1>
        <p className="mt-1 text-sm text-gray-500">Manage your profile information and security preferences.</p>
      </div>

      {/* Profile card */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100 flex items-center gap-2">
          <User size={16} className="text-gray-500" />
          <h2 className="text-base font-semibold text-gray-800">Profile Information</h2>
        </div>

        <div className="px-6 py-2">
          {loadingUser ? (
            <div className="py-6 text-center text-sm text-gray-400">Loading profile…</div>
          ) : user ? (
            <>
              <InfoRow icon={User}      label="Full Name"    value={user.name} />
              <InfoRow icon={User}      label="Username"     value={user.username} />
              <InfoRow icon={Mail}      label="Email"        value={user.email} />
              <InfoRow icon={Building2} label="Organization" value={user.organization_name} />
              <InfoRow icon={Shield}    label="Role"         value={ROLE_LABELS[user.role] || user.role} />
            </>
          ) : (
            <div className="py-6 text-center text-sm text-red-500">Unable to load profile data.</div>
          )}
        </div>
      </div>

      {/* Change password card */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100 flex items-center gap-2">
          <KeyRound size={16} className="text-gray-500" />
          <h2 className="text-base font-semibold text-gray-800">Change Password</h2>
        </div>

        <form onSubmit={handleSubmit} className="px-6 py-5 space-y-5">
          {/* Feedback banners */}
          {successMsg && (
            <div className="flex items-start gap-2 rounded-lg bg-green-50 border border-green-200 px-4 py-3 text-sm text-green-800">
              <CheckCircle2 size={16} className="shrink-0 mt-0.5 text-green-600" />
              {successMsg}
            </div>
          )}
          {errorMsg && (
            <div className="flex items-start gap-2 rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-800">
              <AlertCircle size={16} className="shrink-0 mt-0.5 text-red-600" />
              {errorMsg}
            </div>
          )}

          <PasswordField
            id="current-password"
            label="Current Password"
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            show={showCurrent}
            onToggleShow={() => setShowCurrent((v) => !v)}
            autoComplete="current-password"
          />

          <div>
            <PasswordField
              id="new-password"
              label="New Password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              show={showNew}
              onToggleShow={() => setShowNew((v) => !v)}
              autoComplete="new-password"
            />
            {newPassword.length > 0 && <PasswordStrengthBar password={newPassword} />}
          </div>

          {/* Requirements checklist */}
          {newPassword.length > 0 && (
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 pt-1">
              {PASSWORD_RULES.map((rule) => (
                <RequirementItem key={rule.id} rule={rule} password={newPassword} />
              ))}
            </ul>
          )}

          <div>
            <PasswordField
              id="confirm-password"
              label="Confirm New Password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              show={showConfirm}
              onToggleShow={() => setShowConfirm((v) => !v)}
              autoComplete="new-password"
            />
            {confirmPassword.length > 0 && (
              <p className={`text-xs mt-1.5 flex items-center gap-1 ${passwordsMatch ? 'text-green-600' : 'text-red-500'}`}>
                {passwordsMatch
                  ? <><CheckCircle2 size={13} /> Passwords match</>
                  : <><XCircle size={13} /> Passwords do not match</>}
              </p>
            )}
          </div>

          <div className="pt-1">
            <button
              type="submit"
              disabled={!canSubmit}
              className="w-full sm:w-auto px-6 py-2 rounded-lg bg-primary-600 text-white text-sm font-medium shadow-sm hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {saving ? 'Saving…' : 'Update Password'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default AccountSettings;
